/**
 * Google Analytics 유틸리티
 * @description GA4 초기화 및 이벤트 추적 기능
 * 
 * 각 앱(my-space, investor-space, startup-space)별로 별도의 데이터 스트림 사용
 * 하나의 GA 계정에서 여러 데이터 스트림으로 관리
 */

declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void;
    dataLayer: unknown[];
  }
}

/**
 * GA 초기화 여부
 */
let isInitialized = false;

/**
 * Google Analytics 초기화
 * @param measurementId - GA4 Measurement ID (예: G-XXXXXXXXXX)
 */
export const initGA = (measurementId: string): void => {
  if (typeof window === 'undefined') return;
  if (!measurementId) {
    console.warn('[GA] Measurement ID가 설정되지 않았습니다.');
    return;
  }
  if (isInitialized) {
    console.warn('[GA] 이미 초기화되었습니다.');
    return;
  }

  // gtag.js 스크립트 로드
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // dataLayer 초기화
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // GA4 설정
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // SPA이므로 수동으로 페이지뷰 추적
  });

  isInitialized = true;
  console.log(`[GA] 초기화 완료: ${measurementId}`);
};

/**
 * 페이지뷰 추적
 * @param path - 페이지 경로
 * @param title - 페이지 제목 (선택)
 */
export const trackPageView = (path: string, title?: string): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

/**
 * 커스텀 이벤트 추적
 * @param eventName - 이벤트 이름
 * @param params - 이벤트 파라미터
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, params);
};

/**
 * 사용자 속성 설정
 * @param properties - 사용자 속성
 */
export const setUserProperties = (
  properties: Record<string, unknown>
): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('set', 'user_properties', properties);
};

/**
 * 사용자 ID 설정 (로그인 사용자 추적용)
 * @param userId - 사용자 ID
 */
export const setUserId = (userId: string): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('set', 'user_id', { user_id: userId });
};

/**
 * GA 초기화 상태 확인
 */
export const isGAInitialized = (): boolean => {
  return isInitialized;
};

// 자주 사용하는 이벤트 헬퍼
export const GAEvents = {
  /** 로그인 이벤트 */
  login: (method: string) => trackEvent('login', { method }),
  
  /** 회원가입 이벤트 */
  signUp: (method: string) => trackEvent('sign_up', { method }),
  
  /** 검색 이벤트 */
  search: (searchTerm: string) => trackEvent('search', { search_term: searchTerm }),
  
  /** 버튼 클릭 이벤트 */
  buttonClick: (buttonName: string, location?: string) => 
    trackEvent('button_click', { button_name: buttonName, location }),
  
  /** 에러 이벤트 */
  error: (errorMessage: string, errorCode?: string) => 
    trackEvent('error', { error_message: errorMessage, error_code: errorCode }),
};

